import { useState, useEffect } from "react";
import axios from "axios";
import { X, Mail, Send, Loader2, User, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendEmailModal = ({ isOpen, onClose, business, recipientEmail }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [senderName, setSenderName] = useState("");
  const [senderCompany, setSenderCompany] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [senderEmails, setSenderEmails] = useState([]);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchSenderEmails();
    }
  }, [isOpen]);

  const fetchSenderEmails = async () => {
    try {
      const response = await axios.get(`${API}/sender-emails`);
      setSenderEmails(response.data.sender_emails);
      setSelectedSenderEmail(response.data.default);
    } catch (error) {
      console.error("Failed to fetch sender emails:", error);
    }
  };

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplate(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template");
      return;
    }
    if (!senderName || !senderCompany) {
      toast.error("Please enter your name and company");
      return;
    }

    const payload = {
      template_id: selectedTemplate.id,
      businesses: [{
        id: business.id,
        name: business.name,
        email: recipientEmail,
        address: business.address,
        category: business.category,
        website: business.website
      }],
      sender_name: senderName,
      sender_company: senderCompany,
      sender_email: selectedSenderEmail
    };

    setIsSending(true);
    try {
      // Check for duplicates first
      const checkResponse = await axios.post(`${API}/email/check-duplicates`, payload);
      if (checkResponse.data.duplicate_count > 0) {
        if (!confirm(`This email has already been sent to ${business.name}. Send again anyway?`)) {
          setIsSending(false);
          return;
        }
      }
      
      await axios.post(`${API}/email/send-bulk`, payload);
      toast.success(`Email sent to ${business.name}!`);
      onClose();
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-cyan-500" />
            <h2 className="text-lg font-mono font-bold text-white">Send Email</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 transition-colors">
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Recipient Info */}
          <div className="p-3 bg-zinc-800/50 border border-zinc-700">
            <p className="text-sm text-zinc-400">Sending to:</p>
            <p className="font-mono font-bold text-white">{business?.name}</p>
            <p className="text-sm text-cyan-400">{recipientEmail}</p>
          </div>

          {/* Template Selection */}
          <div>
            <Label className="text-xs font-mono text-zinc-500 uppercase">Select Template</Label>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-zinc-500 py-2">
                No templates found. Create one in Bulk Email first.
              </p>
            ) : (
              <select
                value={selectedTemplate?.id || ""}
                onChange={(e) => {
                  const template = templates.find(t => t.id === e.target.value);
                  setSelectedTemplate(template);
                }}
                className="w-full mt-1 p-2 bg-zinc-900 border border-zinc-700 text-white font-mono text-sm focus:outline-none focus:border-cyan-500"
              >
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Sender Email Selection */}
          <div>
            <Label className="text-xs font-mono text-zinc-500 uppercase">Send From</Label>
            <select
              value={selectedSenderEmail}
              onChange={(e) => setSelectedSenderEmail(e.target.value)}
              className="w-full mt-1 p-2 bg-zinc-900 border border-zinc-700 text-white font-mono text-sm focus:outline-none focus:border-cyan-500"
            >
              {senderEmails.map((sender) => (
                <option key={sender.id} value={sender.email}>
                  {sender.label} ({sender.email})
                </option>
              ))}
            </select>
          </div>

          {/* Sender Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-mono text-zinc-500 uppercase">
                <User className="w-3 h-3 inline mr-1" />
                Your Name
              </Label>
              <Input
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="John Smith"
                className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-mono text-zinc-500 uppercase">
                <Building className="w-3 h-3 inline mr-1" />
                Your Company
              </Label>
              <Input
                value={senderCompany}
                onChange={(e) => setSenderCompany(e.target.value)}
                placeholder="Acme Inc"
                className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono text-sm"
              />
            </div>
          </div>

          {/* Preview */}
          {selectedTemplate && (
            <div className="p-3 bg-zinc-800/30 border border-zinc-700">
              <p className="text-xs text-zinc-500 uppercase mb-1">Subject Preview:</p>
              <p className="text-sm text-white font-mono">
                {selectedTemplate.subject.replace('{business_name}', business?.name || '')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-zinc-800">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-none border-zinc-700 font-mono text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !selectedTemplate || !senderName || !senderCompany}
            className="bg-cyan-500 hover:bg-cyan-600 text-black font-mono rounded-none"
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
